import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import ArticleServiceInstance from "service/article.service";
import DocumentServiceInstance from "service/document.service";
import LoadingSpinner from "../auteurs/components/LoadingSpinner";
import ErrorMessage from "components/errormessages";
import ArticleDetails from "components/articles/ArticleDetails";
import DocumentContentModal from "components/articles/ArticleDocumentModal";
import DocumentEditModal from "components/articles/DocumentEditModal";

const ArticleDetailsPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // États pour les modales
  const [showAddDocumentModal, setShowAddDocumentModal] = useState(false);
  const [showEditDocumentModal, setShowEditDocumentModal] = useState(false);
  const [selectedDocumentId, setSelectedDocumentId] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);

  // Utility functions for display - updated to handle localization objects
  const getArticleTitle = useCallback((article) => {
    if (!article?.title_art) return "Titre non disponible";

    // Handle localized title (assuming fr is primary language)
    if (typeof article.title_art === "object") {
      return (
        article.title_art.fr ||
        article.title_art.en ||
        Object.values(article.title_art)[0] ||
        "Titre non disponible"
      );
    }

    return article.title_art;
  }, []);

  const getArticleAuthor = useCallback((article) => {
    if (article?.author) {
      return `${article.author.title_aut || ""} ${
        article.author.first_name || ""
      } ${article.author.last_name || ""}`.trim();
    }
    return "Auteur non spécifié";
  }, []);

  const getArticleDate = useCallback((article) => {
    if (article?.date_pub_art) {
      return new Date(article.date_pub_art).toLocaleDateString("fr-FR");
    }
    return "Date non spécifiée";
  }, []);

  const getPhotoUrl = useCallback((path) => {
    if (!path) return "";
    let baseUrl = process.env.REACT_APP_API_URL || "https://localhost:5000";
    //remove /api from baseUrl
    baseUrl = baseUrl.replace("/api", "");
    return `${baseUrl}${path}`;
  }, []);

  // Simplified alert function
  const showAlert = useCallback((type, title, message) => {
    Swal.fire({
      icon: type,
      title,
      text: message,
      confirmButtonColor: "#57A863",
      timer: type === "success" ? 2500 : undefined,
      timerProgressBar: type === "success",
    });
  }, []);

  // Fonction pour recharger les données de l'article
  const refreshArticleData = useCallback(async () => {
    try {
      console.log(`🔄 Rechargement des données de l'article: "${slug}"`);
      const response = await ArticleServiceInstance.getAllById(slug);
      
      if (response?.data) {
        setArticle(response.data);
        console.log("✅ Données de l'article rechargées avec succès");
      }
    } catch (error) {
      console.error("❌ Erreur lors du rechargement:", error);
      showAlert("error", "Erreur", "Impossible de recharger les données de l'article");
    }
  }, [slug, showAlert]);

  // Fonction pour gérer l'édition de l'article
  const handleEditArticle = useCallback(() => {
    if (article) {
      navigate(`/admin/article/edit/${article.id_art || slug}`);
    }
  }, [article, navigate, slug]);

  // Fonction pour gérer la suppression de l'article
  const handleDeleteArticle = useCallback(async () => {
    if (!article) return;

    try {
      const result = await Swal.fire({
        title: "Êtes-vous sûr ?",
        text: "Cette action est irréversible. L'article sera définitivement supprimé.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#57A863",
        confirmButtonText: "Oui, supprimer",
        cancelButtonText: "Annuler",
      });

      if (result.isConfirmed) {
        await ArticleServiceInstance.delete(article.id_art || slug);
        
        showAlert("success", "Supprimé", "L'article a été supprimé avec succès");
        
        // Rediriger vers la liste des articles après 1.5 secondes
        setTimeout(() => {
          navigate("/admin/articles/publies");
        }, 1500);
      }
    } catch (error) {
      console.error("Erreur lors de la suppression:", error);
      showAlert("error", "Erreur", "Impossible de supprimer l'article");
    }
  }, [article, slug, navigate, showAlert]);

  // Fonction pour gérer l'ajout de documents
  const handleAddDocument = useCallback(() => {
    if (article) {
      setShowAddDocumentModal(true);
    }
  }, [article]);

  // Fonction pour gérer l'édition d'un document
  const handleEditDocument = useCallback((docId) => {
    if (article && docId) {
      // Trouver le document dans la liste
      const document = article.documents?.find(doc => doc.id_doc === docId);
      
      setSelectedDocumentId(docId);
      setSelectedDocument(document || null);
      setShowEditDocumentModal(true);
    }
  }, [article]);

  // Fonction pour gérer la suppression d'un document
  const handleDeleteDocument = useCallback(async (docId, docTitle) => {
    try {
      const result = await Swal.fire({
        title: "Supprimer le document ?",
        text: `Voulez-vous vraiment supprimer le document "${docTitle}" ?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#57A863",
        confirmButtonText: "Supprimer",
        cancelButtonText: "Annuler",
      });

      if (result.isConfirmed) {
        // Ici vous devrez importer et utiliser votre service de documents
        await DocumentServiceInstance.deleteDocument(docId);
        
        showAlert("success", "Supprimé", "Le document a été supprimé avec succès");
        
        // Recharger les données de l'article pour mettre à jour la liste des documents
        await refreshArticleData();
      }
    } catch (error) {
      console.error("Erreur lors de la suppression du document:", error);
      showAlert("error", "Erreur", "Impossible de supprimer le document");
    }
  }, [showAlert, refreshArticleData]);

  // Gestionnaires pour les modales
  const handleCloseAddDocumentModal = useCallback(() => {
    setShowAddDocumentModal(false);
  }, []);

  const handleCloseEditDocumentModal = useCallback(() => {
    setShowEditDocumentModal(false);
    setSelectedDocumentId(null);
    setSelectedDocument(null);
  }, []);

  const handleDocumentSuccess = useCallback(async (response) => {
    console.log("✅ Document traité avec succès:", response);
    
    // Recharger les données de l'article pour afficher les changements
    await refreshArticleData();
    
    // Fermer les modales
    setShowAddDocumentModal(false);
    setShowEditDocumentModal(false);
    setSelectedDocumentId(null);
    setSelectedDocument(null);
  }, [refreshArticleData]);

  // Load article data
  useEffect(() => {
    const fetchArticle = async () => {
      try {
        setLoading(true);

        if (!slug) {
          throw new Error("Slug manquant dans l'URL");
        }

        console.log(`🔍 Recherche de l'article: "${slug}"`);
        const response = await ArticleServiceInstance.getAllById(slug);

        if (response?.data) {
          setArticle(response.data);

          // Update page title with proper title handling
          const articleTitle =
            typeof response.data.title_art === "object"
              ? response.data.title_art.fr ||
                response.data.title_art.en ||
                "Article"
              : response.data.title_art;

          document.title = `${articleTitle} - Atlas ITDC`;
        } else {
          throw new Error(`Article non trouvé pour le slug: "${slug}"`);
        }
      } catch (error) {
        console.error("Erreur lors de la récupération:", error);

        let errorMessage = "Erreur lors du chargement de l'article";
        if (error.response?.status === 404) {
          errorMessage = `L'article avec le slug "${slug}" n'existe pas`;
        } else if (error.message) {
          errorMessage = error.message;
        }

        setError(errorMessage);
        showAlert("error", "Erreur de chargement", errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [slug, showAlert]);

  // Loading state
  if (loading) {
    return (
      <div className="via-emerald-50 flex min-h-screen items-center justify-center bg-gradient-to-br from-green-50 to-green-100">
        <div className="text-center">
          <LoadingSpinner size="lg" color="#57A863" />
          <p className="mt-4 text-gray-600">
            Chargement des détails de l'article...
          </p>
          <p className="mt-2 text-sm text-gray-500">
            Slug: <span className="font-mono">{slug}</span>
          </p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !article) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-red-50 to-pink-50">
        <div className="mx-4 w-full max-w-lg">
          <ErrorMessage
            title="Article introuvable"
            message={error || `Aucun article trouvé pour le slug: "${slug}"`}
            onRetry={() => window.location.reload()}
            showRetry={true}
          />
          <div className="mt-6 flex justify-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
            >
              ← Retour
            </button>
            <button
              onClick={() => navigate("/admin/articles")}
              className="inline-flex items-center rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-green-700"
            >
              📋 Voir tous les articles
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Main render
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Simple header */}
      <div className="border-b bg-white shadow-sm">
        <div className="mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate(-1)}
                className="inline-flex items-center rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                ← Retour
              </button>
              <h1 className="text-lg font-semibold text-gray-900">
                Détails de l'article{" "}
              </h1>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="mx-auto px-4 py-6 sm:px-6 lg:px-8">
        <ArticleDetails
          article={article}
          getArticleTitle={getArticleTitle}
          getArticleAuthor={getArticleAuthor}
          getArticleDate={getArticleDate}
          getPhotoUrl={getPhotoUrl}
          onEditArticle={handleEditArticle}
          onDeleteArticle={handleDeleteArticle}
          onAddDocument={handleAddDocument}
          onEditDocument={handleEditDocument}
          onDeleteDocument={handleDeleteDocument}
        />
      </div>

      {/* Modales pour les documents */}
      {showAddDocumentModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black bg-opacity-50 p-4">
          <div className="w-full max-w-2xl transform rounded-xl bg-white shadow-2xl transition-all">
            <DocumentContentModal
              onClose={handleCloseAddDocumentModal}
              articleId={article}
              onSuccess={handleDocumentSuccess}
            />
          </div>
        </div>
      )}

      {showEditDocumentModal && selectedDocumentId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black bg-opacity-50 p-4">
          <div className="w-full max-w-2xl transform rounded-xl bg-white shadow-2xl transition-all">
            <DocumentEditModal
              onClose={handleCloseEditDocumentModal}
              documentId={selectedDocumentId}
              articleId={article?.id_art || slug}
              onSuccess={handleDocumentSuccess}
              existingDocument={selectedDocument}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ArticleDetailsPage;